/**
 * @file useIndividualViewNavigation.js
 * @description Navigates to Individual-side routes (/my-assets, /my-benefits,
 * /my-messages, home) and forces a Client Admin out of admin view first, so the
 * landing page, side menu and profile menu match a manual "Switch to Individual View".
 */
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../store/Contexts';
import { LogEventType, UserType } from '../utils/data/enums';
import { logEvent } from '../services/loggingService';

function useIndividualViewNavigation() {
    const navigate = useNavigate();
    const { user, adminView, multitenantFlag, setAdminView } = useAuthContext();

    // Mirrors the profile menu's view-switch eligibility so we never strand a user
    // without an individual record on the Individual side.
    const canSwitchToIndividualView = Boolean(
        user?.jwt?.ind &&
        !multitenantFlag &&
        user?.jwt?.type <= UserType.ClientAdmin &&
        String(user?.jwt?.tenant) === localStorage.getItem('tenantId'),
    );

    const forceIndividualView = () => {
        if (adminView !== true || !canSwitchToIndividualView) {
            return;
        }

        setAdminView(false);
        logEvent(
            LogEventType.AdminIndividualViewChanged,
            'User transitioned from admin to individual view',
        );
    };

    const navigateToIndividualRoute = routePath => {
        forceIndividualView();
        navigate(routePath);
    };

    return {
        canSwitchToIndividualView,
        forceIndividualView,
        navigateToIndividualRoute,
    };
}

export { useIndividualViewNavigation };
